import Image, { StaticImageData } from 'next/image';
import { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import useIsAndroid from '@alltrails/core/hooks/useIsAndroid';
import useIsIos from '@alltrails/core/hooks/useIsIos';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import AppIcon from '@alltrails/denali/components/AppIcon';
import StarRating from '@alltrails/denali/components/StarRating';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
// iOS - Apple Editors' Choice images
import styles from './AppPromotion.module.scss';
import appleEditorsChoiceDaDK from './images/apple-editors-choice-da-DK.svg';
import appleEditorsChoiceDeDE from './images/apple-editors-choice-de-DE.svg';
import appleEditorsChoiceEnGB from './images/apple-editors-choice-en-GB.svg';
import appleEditorsChoiceEnUS from './images/apple-editors-choice-en-US.svg';
import appleEditorsChoiceEsES from './images/apple-editors-choice-es-ES.svg';
import appleEditorsChoiceEsMX from './images/apple-editors-choice-es-MX.svg';
import appleEditorsChoiceFrFR from './images/apple-editors-choice-fr-FR.svg';
import appleEditorsChoiceItIT from './images/apple-editors-choice-it-IT.svg';
import appleEditorsChoiceNbNO from './images/apple-editors-choice-nb-NO.svg';
import appleEditorsChoiceNlNL from './images/apple-editors-choice-nl-NL.svg';
import appleEditorsChoicePlPL from './images/apple-editors-choice-pl-PL.svg';
import appleEditorsChoicePtBR from './images/apple-editors-choice-pt-BR.svg';
import appleEditorsChoicePtPT from './images/apple-editors-choice-pt-PT.svg';
import appleEditorsChoiceSvSE from './images/apple-editors-choice-sv-SE.svg';
// Android - Google Editors' Choice images
import googleEditorsChoiceDaDK from './images/google-editors-choice-da-DK.svg';
import googleEditorsChoiceDeDE from './images/google-editors-choice-de-DE.svg';
import googleEditorsChoiceEnGB from './images/google-editors-choice-en-GB.svg';
import googleEditorsChoiceEnUS from './images/google-editors-choice-en-US.svg';
import googleEditorsChoiceEsES from './images/google-editors-choice-es-ES.svg';
import googleEditorsChoiceEsMX from './images/google-editors-choice-es-MX.svg';
import googleEditorsChoiceFrFR from './images/google-editors-choice-fr-FR.svg';
import googleEditorsChoiceItIT from './images/google-editors-choice-it-IT.svg';
import googleEditorsChoiceNbNO from './images/google-editors-choice-nb-NO.svg';
import googleEditorsChoiceNlNL from './images/google-editors-choice-nl-NL.svg';
import googleEditorsChoicePlPL from './images/google-editors-choice-pl-PL.svg';
import googleEditorsChoicePtBR from './images/google-editors-choice-pt-BR.svg';
import googleEditorsChoicePtPT from './images/google-editors-choice-pt-PT.svg';
import googleEditorsChoiceSvSE from './images/google-editors-choice-sv-SE.svg';

type AppPromotionProps = {
  children?: ReactNode;
  heading?: ReactNode;
};

const appleEditorsChoiceImages: Record<LanguageRegionCode, StaticImageData> = {
  ['da-DK']: appleEditorsChoiceDaDK,
  ['de-DE']: appleEditorsChoiceDeDE,
  ['en-GB']: appleEditorsChoiceEnGB,
  ['en-US']: appleEditorsChoiceEnUS,
  ['es-ES']: appleEditorsChoiceEsES,
  ['es-MX']: appleEditorsChoiceEsMX,
  ['fr-FR']: appleEditorsChoiceFrFR,
  ['it-IT']: appleEditorsChoiceItIT,
  ['nb-NO']: appleEditorsChoiceNbNO,
  ['nl-NL']: appleEditorsChoiceNlNL,
  ['pl-PL']: appleEditorsChoicePlPL,
  ['pt-BR']: appleEditorsChoicePtBR,
  ['pt-PT']: appleEditorsChoicePtPT,
  ['sv-SE']: appleEditorsChoiceSvSE
};

const googleEditorsChoiceImages: Record<LanguageRegionCode, StaticImageData> = {
  ['da-DK']: googleEditorsChoiceDaDK,
  ['de-DE']: googleEditorsChoiceDeDE,
  ['en-GB']: googleEditorsChoiceEnGB,
  ['en-US']: googleEditorsChoiceEnUS,
  ['es-ES']: googleEditorsChoiceEsES,
  ['es-MX']: googleEditorsChoiceEsMX,
  ['fr-FR']: googleEditorsChoiceFrFR,
  ['it-IT']: googleEditorsChoiceItIT,
  ['nb-NO']: googleEditorsChoiceNbNO,
  ['nl-NL']: googleEditorsChoiceNlNL,
  ['pl-PL']: googleEditorsChoicePlPL,
  ['pt-BR']: googleEditorsChoicePtBR,
  ['pt-PT']: googleEditorsChoicePtPT,
  ['sv-SE']: googleEditorsChoiceSvSE
};

export default function AppPromotion({ children, heading }: AppPromotionProps) {
  const languageRegionCode = useLanguageRegionCode();
  const isIos = useIsIos();
  const isAndroid = useIsAndroid();

  // Determine platform (default to iOS for web)
  const isAndroidPlatform = isAndroid && !isIos;

  // Platform-specific values
  const rating = isAndroidPlatform ? 4.7 : 4.9;
  const starRating = isAndroidPlatform ? 4.7 : 5;
  const reviewCount = isAndroidPlatform ? <FormattedMessage defaultMessage="350k+ reviews" /> : <FormattedMessage defaultMessage="1M+ reviews" />;
  const editorsChoiceImage = isAndroidPlatform ? googleEditorsChoiceImages[languageRegionCode] : appleEditorsChoiceImages[languageRegionCode];

  return (
    <div className={styles.container}>
      {children || <AppIcon size={60} />}
      <Typography variant="heading400">{heading || <FormattedMessage defaultMessage="AllTrails is better in the app" />}</Typography>

      <div className={styles.row}>
        <div className={styles.statsContainer}>
          <div className={styles.stats}>
            <div className={styles.rating}>
              <Typography variant="text200bold">
                <FormattedNumber value={rating} />
              </Typography>
              <StarRating rating={starRating} size="sm" />
            </div>
            <Typography color="tertiary" variant="text200">
              {reviewCount}
            </Typography>
          </div>
        </div>
        <div className={styles.dividerContainer}>
          <div className={styles.divider} />
        </div>
        <div className={styles.award}>
          <Image src={editorsChoiceImage} alt="" />
        </div>
      </div>
    </div>
  );
}
